import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, useMemo, useEffect, useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';
// NOTE: SessionMap loads deck.gl + mapbox-gl eagerly (~140 KB gzipped).
// Admin reports is a secondary page, so lazy-loading would trim the main
// bundle for everyone who never visits it. To convert:
//   const SessionMap = React.lazy(() =>
//     import('../../components/analytics/SessionMap').then(m => ({ default: m.SessionMap }))
//   );
// and wrap the <SessionMap ... /> usage in <Suspense fallback={...}>. See
// SessionMap.tsx header for the full playbook.
import { SessionMap, ConcurrencyChart, type ConcurrencyPoint } from '../../components/analytics';
import { usePlatformAdmin } from '../../hooks/usePlatformAdmin';
import styles from '../analytics/Analytics.module.css';

export const Route = createFileRoute('/admin/reports')({
  component: AdminReportsRoute,
});

type TimeRange = '5m' | '15m' | '1h' | '24h' | '7d' | '30d' | '90d';
type GroupBy = '' | 'org' | 'project' | 'app' | 'deploymentProfileType' | 'workerBackendId';
type ChartType = 'bar' | 'sparkline';
/** What the usage chart plots: session-minutes, or concurrent sessions (peak / P90 / mean). */
type ChartMetric = 'minutes' | 'concurrent';

const COLOR_SCHEMES = {
  earthy: [
    'var(--text-primary)',
    'var(--color-accent)',
    '#E63946', '#457B9D', '#F4A261', '#2A9D8F', '#6D597A', '#B56576',
    '#355070', '#EAAC8B', '#52796F', '#BC6C25', '#D62828', '#003049',
    '#FCBF49', '#006D77', '#9B5DE5', '#F15BB5', '#1D3557', '#FFB703',
    '#386641', '#E07A5F', '#264653', '#E76F51', '#2EC4B6', '#8338EC',
    '#FF006E', '#84A98C', '#A44A3F', '#577590', '#F3722C', '#43AA8B',
    '#F94144', '#90BE6D', '#F9C74F', '#277DA1', '#9B2335',
  ],
  vivid: [
    '#F72585', '#7209B7', '#3A0CA3', '#4361EE', '#4CC9F0', '#F77F00',
    '#FCBF49', '#06D6A0', '#EF476F', '#118AB2', '#073B4C', '#FFD166',
    '#E71D36', '#2EC4B6', '#FF9F1C', '#5E60CE', '#6930C3', '#5390D9',
    '#48BFE3', '#56CFE1', '#64DFDF', '#72EFDD', '#80FFDB', '#FF0A54',
    '#FF477E', '#FF5C8A', '#FF7096', '#FF85A1', '#E63946', '#1D3557',
    '#A8DADC', '#457B9D', '#F1FAEE', '#2A9D8F', '#E9C46A',
  ],
} as const;

type ColorScheme = keyof typeof COLOR_SCHEMES;

interface UsageDataPoint {
  timestamp: number;
  sessions: number;
  minutes: number;
  billableMinutes: number;
  group?: string;
}

interface ReportsMetrics {
  minutesDay: number;
  minutesWeek: number;
  minutesMonth: number;
  billableMinutesDay: number;
  billableMinutesWeek: number;
  billableMinutesMonth: number;
  sessionsDay: number;
  sessionsWeek: number;
  sessionsMonth: number;
}

interface ReportsConnection {
  sessionId: string;
  browserColo: string | null;
  browserH3: string | null;
  workerColo: string | null;
  workerH3: string | null;
  sessionColo: string | null;
  sessionH3: string | null;
  /** Per-session value of the active groupBy column. Null when groupBy is unset. */
  group?: string | null;
}

interface ReportsData {
  metrics: ReportsMetrics;
  avgDurationMs: number | null;
  usage: UsageDataPoint[];
  /** Concurrent sessions per bucket (peak / P90 / mean); present when requested with concurrency=1. */
  concurrency?: ConcurrencyPoint[];
  connections: ReportsConnection[];
}

interface FilterOptions {
  orgs: Array<{ id: string; name: string }>;
  projectIds: string[];
  appIds: string[];
  deploymentProfileTypes: string[];
  workerBackendIds: string[];
}

function getApiBaseUrl(): string {
  const wsUrl = (import.meta as any).env.VITE_API_URL || 'wss://api.dev.interlucent.ai';
  return wsUrl.replace(/^wss:/, 'https:').replace(/^ws:/, 'http:');
}

function formatNumber(n: number): string {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n.toString();
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60000).toFixed(1)}m`;
}

function formatTime(timestamp: number, range: TimeRange): string {
  const date = new Date(timestamp);
  if (range === '5m' || range === '15m') {
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  }
  if (range === '1h') {
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  }
  if (range === '24h') {
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  }
  if (range === '7d') {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  }
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// ── Shared data hook ───────────────────────────────────────────────────
function useChartData(data: UsageDataPoint[]) {
  return useMemo(() => {
    if (data.length === 0) {
      return { timestamps: [] as number[], groups: [] as string[], stackedData: new Map<number, Map<string, number>>(), maxTotal: 1 };
    }
    const tsSet = new Set<number>();
    const grpSet = new Set<string>();
    for (const d of data) {
      tsSet.add(d.timestamp);
      if (d.group) grpSet.add(d.group);
    }
    const timestamps = Array.from(tsSet).sort((a, b) => a - b);
    const groups = grpSet.size > 0 ? Array.from(grpSet) : ['_total'];

    const lookup = new Map<number, Map<string, number>>();
    for (const ts of timestamps) lookup.set(ts, new Map());
    for (const d of data) {
      const grp = d.group || '_total';
      const tsMap = lookup.get(d.timestamp);
      if (tsMap) tsMap.set(grp, (tsMap.get(grp) || 0) + d.minutes);
    }

    let maxTotal = 1;
    for (const [, grpMap] of lookup) {
      let total = 0;
      for (const v of grpMap.values()) total += v;
      if (total > maxTotal) maxTotal = total;
    }
    return { timestamps, groups, stackedData: lookup, maxTotal };
  }, [data]);
}

// ── Stacked Bar Chart ──────────────────────────────────────────────────
function StackedBarChart({
  data,
  range,
  entityLabels,
  colorScheme = 'earthy',
  width = 800,
  height = 300,
}: {
  data: UsageDataPoint[];
  range: TimeRange;
  entityLabels: Record<string, string>;
  colorScheme?: ColorScheme;
  width?: number;
  height?: number;
}) {
  const [hovered, setHovered] = useState<{ bucket: number; group: string } | null>(null);

  const padding = { top: 20, right: 40, bottom: 40, left: 60 };
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;

  const { timestamps, groups, stackedData, maxTotal } = useChartData(data);

  if (timestamps.length === 0) {
    return (
      <svg viewBox={`0 0 ${width} ${height}`} className={styles.chart} preserveAspectRatio="xMidYMid meet">
        <text x={width / 2} y={height / 2} textAnchor="middle" className={styles.emptyMessage}>
          No usage data for this period
        </text>
      </svg>
    );
  }

  // Bucket width label
  const bucketMs = timestamps.length >= 2 ? timestamps[1] - timestamps[0] : 0;
  const bucketLabel = bucketMs >= 86400000 ? `${Math.round(bucketMs / 86400000)}d`
    : bucketMs >= 3600000 ? `${Math.round(bucketMs / 3600000)}h`
    : bucketMs >= 60000 ? `${Math.round(bucketMs / 60000)}m`
    : '';

  const barGap = Math.max(1, Math.min(3, chartWidth / timestamps.length * 0.08));
  const barWidth = Math.max(2, (chartWidth / timestamps.length) - barGap);
  const scaleY = (val: number) => padding.top + chartHeight - (val / maxTotal) * chartHeight;

  const yTicks: number[] = [];
  const yStep = Math.ceil(maxTotal / 5) || 1;
  for (let i = 0; i <= maxTotal; i += yStep) yTicks.push(i);

  const xLabelCount = Math.min(6, timestamps.length);
  const xLabelStep = Math.max(1, Math.floor(timestamps.length / xLabelCount));

  const palette = COLOR_SCHEMES[colorScheme];

  return (
    <div>
      <svg viewBox={`0 0 ${width} ${height}`} className={styles.chart} preserveAspectRatio="xMidYMid meet" onMouseLeave={() => setHovered(null)}>
        {/* Grid */}
        <g className={styles.gridLines}>
          {yTicks.map((tick, i) => <line key={i} x1={padding.left} y1={scaleY(tick)} x2={width - padding.right} y2={scaleY(tick)} />)}
        </g>
        {/* Y axis */}
        <g className={styles.axis}>
          <line x1={padding.left} y1={padding.top} x2={padding.left} y2={height - padding.bottom} />
          {yTicks.map((tick, i) => <text key={i} x={padding.left - 10} y={scaleY(tick)} textAnchor="end" dominantBaseline="middle">{formatNumber(tick)}</text>)}
          <text x={padding.left - 45} y={height / 2} textAnchor="middle" transform={`rotate(-90, ${padding.left - 45}, ${height / 2})`} className={styles.axisLabel}>MINUTES</text>
        </g>
        {/* X axis */}
        <g className={styles.axis}>
          <line x1={padding.left} y1={height - padding.bottom} x2={width - padding.right} y2={height - padding.bottom} />
          {timestamps.map((ts, i) => {
            if (i % xLabelStep !== 0 && i !== timestamps.length - 1) return null;
            const x = padding.left + i * (barWidth + barGap) + barWidth / 2;
            return <text key={i} x={x} y={height - padding.bottom + 20} textAnchor="middle">{formatTime(ts, range)}</text>;
          })}
        </g>
        {/* Bucket width indicator */}
        {bucketLabel && (
          <text x={width - padding.right} y={padding.top - 6} textAnchor="end" className={styles.bucketLabel}>
            {bucketLabel} buckets
          </text>
        )}
        {/* Bars */}
        {timestamps.map((ts, bi) => {
          const grpMap = stackedData.get(ts);
          if (!grpMap) return null;
          const x = padding.left + bi * (barWidth + barGap);
          let base = 0;
          return (
            <g key={bi}>
              {groups.map((grp, gi) => {
                const val = grpMap.get(grp) || 0;
                if (val === 0) { return null; }
                const segY = scaleY(base + val);
                const segH = scaleY(base) - segY;
                base += val;
                const isSegHovered = hovered?.bucket === bi && hovered?.group === grp;
                const isBucketHovered = hovered?.bucket === bi;
                return (
                  <rect
                    key={grp}
                    x={x}
                    y={segY}
                    width={barWidth}
                    height={Math.max(0, segH)}
                    fill={palette[gi % palette.length]}
                    opacity={isSegHovered ? 1 : isBucketHovered ? 0.55 : 0.82}
                    stroke={isSegHovered ? 'var(--text-primary)' : palette[gi % palette.length]}
                    strokeWidth={isSegHovered ? 1.5 : 0.5}
                    strokeOpacity={isSegHovered ? 0.8 : 0.3}
                    onMouseEnter={() => setHovered({ bucket: bi, group: grp })}
                  />
                );
              })}
            </g>
          );
        })}
        {/* Tooltip — single segment */}
        {hovered && (() => {
          const { bucket: bi, group: grp } = hovered;
          const ts = timestamps[bi];
          const val = stackedData.get(ts)?.get(grp) || 0;
          const gi = groups.indexOf(grp);
          const color = palette[gi % palette.length];
          const label = grp === '_total' ? 'Total' : (entityLabels[grp] || grp);
          const tooltipX = padding.left + bi * (barWidth + barGap) + barWidth / 2;
          const valueText = `${label}: ${val} min`;
          const tooltipW = Math.max(100, Math.min(300, valueText.length * 7 + 20));
          const tooltipH = 32;
          const clampedX = Math.max(padding.left + tooltipW / 2, Math.min(width - padding.right - tooltipW / 2, tooltipX));
          return (
            <g className={styles.tooltip}>
              <rect x={clampedX - tooltipW / 2} y={padding.top} width={tooltipW} height={tooltipH} className={styles.tooltipBg} rx={2} />
              <text x={clampedX} y={padding.top + 12} textAnchor="middle" className={styles.tooltipTitle}>{formatTime(ts, range)}</text>
              <text x={clampedX} y={padding.top + 26} textAnchor="middle" className={styles.tooltipValue}>
                <tspan fill={color}>{label}</tspan>: {val} min
              </text>
            </g>
          );
        })()}
      </svg>
      {groups.length > 1 && (
        <div className={styles.chartLegend}>
          {groups.map((grp, i) => (
            <div key={grp} className={styles.legendItem}>
              <span className={styles.legendSwatch} style={{ background: palette[i % palette.length] }} />
              <span>{entityLabels[grp] || grp}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Sparkline Grid ─────────────────────────────────────────────────────
function SparklineGrid({
  data,
  range,
  entityLabels,
  colorScheme = 'earthy',
  activeFilter,
  onToggleFilter,
}: {
  data: UsageDataPoint[];
  range: TimeRange;
  entityLabels: Record<string, string>;
  colorScheme?: ColorScheme;
  activeFilter?: string;
  onToggleFilter?: (groupId: string) => void;
}) {
  const { timestamps, groups, stackedData } = useChartData(data);
  const palette = COLOR_SCHEMES[colorScheme];

  if (timestamps.length === 0) {
    return <div className={styles.emptyState}>No usage data for this period</div>;
  }

  const sparkW = 240;
  const sparkH = 48;
  const pad = { top: 4, right: 4, bottom: 4, left: 4 };
  const innerW = sparkW - pad.left - pad.right;
  const innerH = sparkH - pad.top - pad.bottom;

  return (
    <div className={styles.sparklineGrid}>
      {groups.map((grp, gi) => {
        const color = palette[gi % palette.length];
        const values = timestamps.map(ts => stackedData.get(ts)?.get(grp) || 0);
        const maxVal = Math.max(1, ...values);
        const total = values.reduce((a, b) => a + b, 0);

        const barW = Math.max(1, (innerW / values.length) - 1);
        const barGap = Math.max(0.5, (innerW - barW * values.length) / Math.max(1, values.length - 1));

        const isActive = activeFilter === grp;
        const isDimmed = activeFilter && activeFilter !== grp;

        return (
          <div
            key={grp}
            className={`${styles.sparklineCard} ${isActive ? styles.sparklineCardActive : ''} ${isDimmed ? styles.sparklineCardDimmed : ''}`}
            onClick={() => onToggleFilter?.(grp)}
            style={{ cursor: onToggleFilter ? 'pointer' : undefined }}
          >
            <div className={styles.sparklineMeta}>
              <span className={styles.legendSwatch} style={{ background: color }} />
              <span className={styles.sparklineLabel}>{entityLabels[grp] || grp}</span>
              <span className={styles.sparklineTotal}>{formatNumber(total)} min</span>
            </div>
            <svg viewBox={`0 0 ${sparkW} ${sparkH}`} className={styles.sparklineSvg} preserveAspectRatio="none">
              {values.map((v, i) => {
                const h = (v / maxVal) * innerH;
                return (
                  <rect
                    key={i}
                    x={pad.left + i * (barW + barGap)}
                    y={pad.top + innerH - h}
                    width={barW}
                    height={Math.max(0, h)}
                    fill={color}
                    opacity={0.75}
                  />
                );
              })}
            </svg>
            <div className={styles.sparklineTimeRange}>
              <span>{formatTime(timestamps[0], range)}</span>
              <span>{formatTime(timestamps[timestamps.length - 1], range)}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Main Component ──────────────────────────────────────────────────────
function AdminReportsRoute() {
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const { isPlatformAdmin } = usePlatformAdmin();

  const [timeRange, setTimeRange] = useState<TimeRange>('24h');
  const [groupBy, setGroupBy] = useState<GroupBy>('org');
  const [chartType, setChartType] = useState<ChartType>('sparkline');
  // Sessions view has its own chart; chart type applies to minutes only.
  const [chartMetric, setChartMetric] = useState<ChartMetric>('minutes');
  const [colorScheme, setColorScheme] = useState<ColorScheme>('earthy');
  const [mapMode, setMapMode] = useState<'full' | 'browser-only'>('full');
  const [showSessionArcs, setShowSessionArcs] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reportsData, setReportsData] = useState<ReportsData | null>(null);
  const [filterOptions, setFilterOptions] = useState<FilterOptions | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [filterOrgId, setFilterOrgId] = useState('');
  const [filterProjectId, setFilterProjectId] = useState('');
  const [filterAppId, setFilterAppId] = useState('');
  const [filterPoolType, setFilterPoolType] = useState('');
  const [filterBackendId, setFilterBackendId] = useState('');

  // Redirect non-admins
  useEffect(() => {
    if (isPlatformAdmin === false) navigate({ to: '/' });
  }, [isPlatformAdmin, navigate]);

  const authHeaders = useCallback(async () => {
    const token = await getToken({ template: 'default' });
    if (!token) throw new Error('Authentication required');
    return { Authorization: `Bearer ${token}` };
  }, [getToken]);

  // Fetch filter options on mount
  useEffect(() => {
    (async () => {
      try {
        const headers = await authHeaders();
        const res = await fetch(`${getApiBaseUrl()}/api/admin/reports/filter-options`, { headers });
        if (res.ok) setFilterOptions(await res.json() as FilterOptions);
      } catch { /* ignore */ }
    })();
  }, [authHeaders]);

  // Fetch reports data
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const headers = await authHeaders();
      const params = new URLSearchParams({ range: timeRange });
      if (groupBy) params.set('groupBy', groupBy);
      if (filterOrgId) params.set('orgId', filterOrgId);
      if (filterProjectId) params.set('projectId', filterProjectId);
      if (filterAppId) params.set('appId', filterAppId);
      if (filterPoolType) params.set('deploymentProfileType', filterPoolType);
      if (filterBackendId) params.set('workerBackendId', filterBackendId);
      if (chartMetric === 'concurrent') params.set('concurrency', '1');

      const res = await fetch(`${getApiBaseUrl()}/api/admin/reports?${params}`, { headers });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setReportsData(await res.json() as ReportsData);
    } catch (err) {
      setError(String(err));
    } finally {
      setLoading(false);
    }
  }, [authHeaders, timeRange, groupBy, filterOrgId, filterProjectId, filterAppId, filterPoolType, filterBackendId, chartMetric]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const metrics = reportsData?.metrics;
  const hasActiveFilters = filterOrgId || filterProjectId || filterAppId || filterPoolType || filterBackendId;

  const timeRanges: { value: TimeRange; label: string }[] = [
    { value: '5m', label: '5M' },
    { value: '15m', label: '15M' },
    { value: '1h', label: '1H' },
    { value: '24h', label: '24H' },
    { value: '7d', label: '7D' },
    { value: '30d', label: '30D' },
    { value: '90d', label: '90D' },
  ];

  const chartMetricOptions: { value: ChartMetric; label: string }[] = [
    { value: 'minutes', label: 'Minutes' },
    { value: 'concurrent', label: 'Sessions' },
  ];

  const groupByOptions: { value: GroupBy; label: string }[] = [
    { value: '', label: 'Total' },
    { value: 'org', label: 'Org' },
    { value: 'project', label: 'Project' },
    { value: 'app', label: 'App' },
    { value: 'deploymentProfileType', label: 'Pool Type' },
    { value: 'workerBackendId', label: 'Backend' },
  ];

  const entityLabels: Record<string, string> = useMemo(() => {
    const labels: Record<string, string> = {};
    if (filterOptions?.orgs) {
      for (const { id, name } of filterOptions.orgs) {
        labels[id] = name;
      }
    }
    return labels;
  }, [filterOptions]);

  // Build group value → palette color, matching the chart's group ordering.
  // The charts (StackedBarChart / SparklineGrid) compute their own `groups`
  // order by iterating `usage` data points and collecting distinct group
  // values into a Set; replicate that here so the SessionMap and the chart
  // legend agree on which color belongs to which group.
  const groupColorMap = useMemo(() => {
    if (!groupBy) return undefined;
    const palette = COLOR_SCHEMES[colorScheme];
    const seen = new Set<string>();
    for (const point of reportsData?.usage ?? []) {
      if (point.group) seen.add(point.group);
    }
    const result: Record<string, string> = {};
    let i = 0;
    for (const g of seen) {
      result[g] = palette[i % palette.length];
      i++;
    }
    return result;
  }, [groupBy, colorScheme, reportsData?.usage]);

  const filterForGroupBy: Record<GroupBy, { value: string; set: (v: string) => void }> = {
    '': { value: '', set: () => {} },
    org: { value: filterOrgId, set: setFilterOrgId },
    project: { value: filterProjectId, set: setFilterProjectId },
    app: { value: filterAppId, set: setFilterAppId },
    deploymentProfileType: { value: filterPoolType, set: setFilterPoolType },
    workerBackendId: { value: filterBackendId, set: setFilterBackendId },
  };

  const handleSparklineToggle = useCallback((groupId: string) => {
    const f = filterForGroupBy[groupBy];
    if (!f) return;
    f.set(f.value === groupId ? '' : groupId);
  }, [groupBy, filterOrgId, filterProjectId, filterAppId, filterPoolType, filterBackendId]);

  const selectedRangeLabel = timeRange === '5m' ? 'LAST 5 MIN'
    : timeRange === '15m' ? 'LAST 15 MIN'
    : timeRange === '1h' ? 'LAST HOUR'
    : timeRange === '24h' ? 'LAST 24H'
    : timeRange === '7d' ? 'LAST 7 DAYS'
    : timeRange === '30d' ? 'LAST 30 DAYS'
    : 'LAST 90 DAYS';

  const minutesSelectedRange = useMemo(() => {
    const usage = reportsData?.usage ?? [];
    return usage.reduce((sum, d) => sum + d.minutes, 0);
  }, [reportsData]);

  const billableMinutesSelectedRange = useMemo(() => {
    const usage = reportsData?.usage ?? [];
    return usage.reduce((sum, d) => sum + d.billableMinutes, 0);
  }, [reportsData]);

  if (isPlatformAdmin === undefined) return null;

  return (
    <div className={styles.container}>
      {/* Header */}
      <header className={styles.header}>
        <div className={styles.headerContent}>
          <h1 className={styles.title}>Platform Reports</h1>
          <p className={styles.subtitle}>Cross-organization usage analytics from the billing database.</p>
        </div>
        <div className={styles.timeRangeSelector}>
          {timeRanges.map(({ value, label }) => (
            <button
              key={value}
              className={`${styles.timeRangeButton} ${timeRange === value ? styles.active : ''}`}
              onClick={() => setTimeRange(value)}
            >
              {label}
            </button>
          ))}
        </div>
      </header>

      {/* Error */}
      {error && (
        <div className={styles.errorBanner}>
          <span>Failed to load reports: {error}</span>
          <button className={styles.retryButton} onClick={fetchData}>Retry</button>
        </div>
      )}

      {/* Filters */}
      <div className={styles.filterBar}>
        <select className={styles.filterSelect} value={filterOrgId} onChange={e => setFilterOrgId(e.target.value)}>
          <option value="">All Organizations</option>
          {filterOptions?.orgs?.map(({ id, name }) => <option key={id} value={id}>{name}</option>)}
        </select>
        <select className={styles.filterSelect} value={filterProjectId} onChange={e => setFilterProjectId(e.target.value)}>
          <option value="">All Projects</option>
          {filterOptions?.projectIds.map(id => <option key={id} value={id}>{id}</option>)}
        </select>
        <select className={styles.filterSelect} value={filterAppId} onChange={e => setFilterAppId(e.target.value)}>
          <option value="">All Applications</option>
          {filterOptions?.appIds.map(id => <option key={id} value={id}>{id}</option>)}
        </select>
        <select className={styles.filterSelect} value={filterPoolType} onChange={e => setFilterPoolType(e.target.value)}>
          <option value="">All Pool Types</option>
          {filterOptions?.deploymentProfileTypes.map(id => <option key={id} value={id}>{id}</option>)}
        </select>
        <select className={styles.filterSelect} value={filterBackendId} onChange={e => setFilterBackendId(e.target.value)}>
          <option value="">All Backends</option>
          {filterOptions?.workerBackendIds.map(id => <option key={id} value={id}>{id}</option>)}
        </select>
        {hasActiveFilters && (
          <button className={styles.filterClear} onClick={() => { setFilterOrgId(''); setFilterProjectId(''); setFilterAppId(''); setFilterPoolType(''); setFilterBackendId(''); }}>
            Clear
          </button>
        )}
      </div>

      {/* Hero Metrics */}
      <section className={styles.heroMetricsRow}>
        <div className={styles.heroMetricsGroup}>
          <div className={`${styles.metricHero} ${loading ? styles.loading : ''}`}>
            <div className={styles.metricLabel}>{selectedRangeLabel}</div>
            <div className={styles.metricValue}>{formatNumber(billableMinutesSelectedRange)}</div>
            <div className={styles.metricUnit}>billable minutes</div>
            {minutesSelectedRange !== billableMinutesSelectedRange && (
              <div className={styles.metricSecondary}>{formatNumber(minutesSelectedRange)} session min</div>
            )}
          </div>

          <div className={`${styles.metricHero} ${loading ? styles.loading : ''}`}>
            <div className={styles.metricLabel}>AVG DURATION</div>
            <div className={styles.metricValue}>
              {reportsData?.avgDurationMs != null ? formatDuration(reportsData.avgDurationMs) : '--'}
            </div>
            <div className={styles.metricUnit}>per session</div>
          </div>
        </div>

        <div className={styles.heroMetricsGroupFixed}>
          <div className={`${styles.metricHeroCompact} ${loading ? styles.loading : ''}`}>
            <div className={styles.metricLabel}>TODAY</div>
            <div className={styles.metricValue}>{formatNumber(metrics?.billableMinutesDay ?? 0)}</div>
            <div className={styles.metricUnit}>billable min</div>
            <div className={styles.metricSecondary}>{formatNumber(metrics?.minutesDay ?? 0)} session min</div>
          </div>
          <div className={`${styles.metricHeroCompact} ${loading ? styles.loading : ''}`}>
            <div className={styles.metricLabel}>THIS WEEK</div>
            <div className={styles.metricValue}>{formatNumber(metrics?.billableMinutesWeek ?? 0)}</div>
            <div className={styles.metricUnit}>billable min</div>
            <div className={styles.metricSecondary}>{formatNumber(metrics?.minutesWeek ?? 0)} session min</div>
          </div>
          <div className={`${styles.metricHeroCompact} ${loading ? styles.loading : ''}`}>
            <div className={styles.metricLabel}>THIS MONTH</div>
            <div className={styles.metricValue}>{formatNumber(metrics?.billableMinutesMonth ?? 0)}</div>
            <div className={styles.metricUnit}>billable min</div>
            <div className={styles.metricSecondary}>{formatNumber(metrics?.minutesMonth ?? 0)} session min</div>
          </div>
        </div>
      </section>

      {/* Usage Chart */}
      <section className={styles.chartSection}>
        <div className={styles.chartHeaderRow}>
          <div>
            <h2 className={styles.chartTitle}>{chartMetric === 'concurrent' ? 'Usage Sessions' : 'Usage Minutes'}</h2>
            <p className={styles.chartDescription}>
              {chartMetric === 'concurrent'
                ? 'Sessions running at the same time in each interval, all organizations: the mean over every minute, the level 90% of minutes stay at or below (P90), and the busiest minute (peak).'
                : 'Session minutes over the selected period, all organizations.'}
            </p>
          </div>
          <div className={styles.chartControls}>
            <div className={styles.groupBySelector} role="group" aria-label="Chart metric">
              {chartMetricOptions.map(({ value, label }) => (
                <button
                  key={value}
                  className={`${styles.groupByButton} ${chartMetric === value ? styles.active : ''}`}
                  onClick={() => setChartMetric(value)}
                  aria-pressed={chartMetric === value}
                >
                  {label}
                </button>
              ))}
            </div>
            <div className={styles.groupBySelector}>
              {groupByOptions.map(({ value, label }) => (
                <button
                  key={value}
                  className={`${styles.groupByButton} ${groupBy === value ? styles.active : ''}`}
                  onClick={() => setGroupBy(value)}
                >
                  {label}
                </button>
              ))}
            </div>
            {/* Chart type applies to the minutes chart only. */}
            {chartMetric !== 'concurrent' && (
              <select
                className={styles.filterSelect}
                value={chartType}
                onChange={e => setChartType(e.target.value as ChartType)}
                style={{ width: 'auto', minWidth: 0 }}
              >
                <option value="bar">Stacked Bar</option>
                <option value="sparkline">Sparklines</option>
              </select>
            )}
            <select
              className={styles.filterSelect}
              value={colorScheme}
              onChange={e => setColorScheme(e.target.value as ColorScheme)}
              style={{ width: 'auto', minWidth: 0 }}
            >
              <option value="earthy">Earthy</option>
              <option value="vivid">Vivid</option>
            </select>
          </div>
        </div>
        <div className={styles.chartContainer}>
          {chartMetric === 'concurrent' ? (
            <ConcurrencyChart data={reportsData?.concurrency ?? []} formatTime={ts => formatTime(ts, timeRange)} />
          ) : chartType === 'bar' ? (
            <StackedBarChart data={reportsData?.usage ?? []} range={timeRange} entityLabels={entityLabels} colorScheme={colorScheme} />
          ) : (
            <SparklineGrid data={reportsData?.usage ?? []} range={timeRange} entityLabels={entityLabels} colorScheme={colorScheme} activeFilter={groupBy ? filterForGroupBy[groupBy].value : undefined} onToggleFilter={groupBy ? handleSparklineToggle : undefined} />
          )}
        </div>
      </section>

      {/* Session Map */}
      <section className={styles.chartSection}>
        <div className={styles.chartHeaderRow}>
          <div>
            <h2 className={styles.chartTitle}>Session Geography</h2>
            <p className={styles.chartDescription}>
              {mapMode === 'browser-only'
                ? 'Approximate user geolocation for the selected period.'
                : 'Browser and worker locations for the selected period.'}
            </p>
          </div>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            {mapMode === 'full' && (
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: 'var(--text-sm)', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={showSessionArcs}
                  onChange={e => setShowSessionArcs(e.target.checked)}
                  style={{ cursor: 'pointer', accentColor: 'var(--text-primary)' }}
                />
                Show sessions
              </label>
            )}
            <select
              className={styles.filterSelect}
              value={mapMode}
              onChange={e => setMapMode(e.target.value as 'full' | 'browser-only')}
              style={{ width: 'auto', minWidth: 0 }}
              aria-label="Map view"
            >
              <option value="full">Full session placements</option>
              <option value="browser-only">User geography</option>
            </select>
          </div>
        </div>
        <SessionMap
          connections={reportsData?.connections}
          isLoading={loading}
          mode={mapMode}
          showSessions={showSessionArcs}
          groupBy={groupBy || undefined}
          groupColors={groupColorMap}
          groupLabels={entityLabels}
        />
      </section>
    </div>
  );
}
